<template>
  <div v-if="modelValue && fellow" class="fellow-preview-overlay" @click.self="close">
    <div class="fellow-preview-dialog" role="dialog" aria-modal="true" aria-label="Fellow profile preview">
      <div class="dialog-header">
        <div class="header-profile">
          <div class="avatar-wrap">
            <img
              class="avatar"
              :src="resolvedAvatar"
              :alt="displayName"
              @error="handleAvatarError"
            >
            <div class="avatar-status"></div>
          </div>
          <div class="header-copy">
            <h4 class="title">{{ displayName }}</h4>
            <p class="subtitle">{{ currentJobTitle }}</p>
          </div>
        </div>
        <button type="button" class="close-btn" @click="close" aria-label="Close preview">
          &times;
        </button>
      </div>

      <div class="dialog-body">
        <div class="tab-nav">
          <button
            type="button"
            class="tab-btn"
            :class="{ active: activeTab === 'personal' }"
            @click="activeTab = 'personal'"
          >
            Personal Information
          </button>
          <button
            type="button"
            class="tab-btn"
            :class="{ active: activeTab === 'fellowship' }"
            @click="activeTab = 'fellowship'"
          >
            Fellowship Details
          </button>
        </div>

        <div v-if="activeTab === 'personal'" class="content-pane">
          <div class="grid">
            <div class="info-box">
              <p class="label">Full Name</p>
              <p class="value">{{ displayName }}</p>
            </div>

            <div class="info-box" v-if="fellow.country">
              <p class="label">Country</p>
              <p class="value">{{ countryName }}</p>
            </div>

            <div class="info-box" v-if="fellowDetails.current_organisation">
              <p class="label">Current Organisation</p>
              <p class="value">{{ fellowDetails.current_organisation }}</p>
            </div>

            <div class="info-box" v-if="fellowDetails.current_job_title">
              <p class="label">Current Job Title</p>
              <p class="value">{{ fellowDetails.current_job_title }}</p>
            </div>

            <div class="info-box" v-if="fellow.fellowship_year">
              <p class="label">Fellowship Year</p>
              <p class="value">{{ fellow.fellowship_year }}</p>
            </div>
          </div>
        </div>

        <div v-if="activeTab === 'fellowship'" class="content-pane">
          <div v-if="fellowshipEntries.length" class="section-block">
            <div v-if="fellowshipEntries.length > 1" class="fellowship-tabs">
              <button
                v-for="(entry, index) in fellowshipEntries"
                :key="`fellowship-tab-${index}`"
                type="button"
                class="tab-btn compact"
                :class="{ active: activeFellowshipIndex === index }"
                @click="activeFellowshipIndex = index"
              >
                Fellowship {{ index + 1 }}
              </button>
            </div>

            <div class="grid grid-2col" v-if="activeFellowshipEntry">
              <div class="info-box" v-if="activeFellowshipEntry.edctp_project_title">
                <p class="label">Project Title</p>
                <p class="value">{{ activeFellowshipEntry.edctp_project_title }}</p>
              </div>

              <div class="info-box" v-if="activeFellowshipEntry.edctp_project_call">
                <p class="label">Fellowship Type</p>
                <p class="value">{{ getFellowshipTypeLabel(activeFellowshipEntry.edctp_project_call) }}</p>
              </div>
            </div>

            <div class="grid" v-if="activeFellowshipEntry">
              <div class="info-box" v-if="activeFellowshipEntry.edctp_programme">
                <p class="label">EDCTP Program</p>
                <p class="value">{{ getProgramLabel(activeFellowshipEntry.edctp_programme) }}</p>
              </div>

              <div class="info-box" v-if="activeFellowshipEntry.edctp_project_code">
                <p class="label">EDCTP Project Code</p>
                <p class="value">{{ activeFellowshipEntry.edctp_project_code }}</p>
              </div>

              <div class="info-box" v-if="hostOrganisations.length">
                <p class="label">Host Organisation</p>
                <p class="value">{{ hostOrganisations.join(', ') }}</p>
              </div>

              <div class="info-box" v-if="activeFellowshipEntry.research_theme">
                <p class="label">Research Theme</p>
                <p class="value">{{ activeFellowshipEntry.research_theme }}</p>
              </div>

              <div class="info-box" v-if="activeFellowshipEntry.project_status">
                <p class="label">Status</p>
                <p class="value">{{ activeFellowshipEntry.project_status }}</p>
              </div>
            </div>

            <div class="section-block" v-if="activeFellowshipEntry.objectives">
              <h5 class="section-title">Project Objectives</h5>
              <p class="value">{{ activeFellowshipEntry.objectives }}</p>
            </div>

            <div class="section-block" v-if="activeFellowshipEntry.project_summary">
              <h5 class="section-title">Project Summary</h5>
              <p class="value">{{ activeFellowshipEntry.project_summary }}</p>
            </div>

            <div class="section-block" v-if="activeFellowshipEntry.results_and_outcomes">
              <h5 class="section-title">Results and Outcomes</h5>
              <p class="value">{{ activeFellowshipEntry.results_and_outcomes }}</p>
            </div>
          </div>

          <p v-else class="empty-copy">No fellowship details provided.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getFellowshipTypeDescription } from '@/utils/fellowshipTypeMap'
import { getCountryName } from '@/utils/countryMap'
import { sortFellowshipsByCallYear } from '@/utils/fellowshipOrder'

export default {
  name: 'FellowPreviewDialog',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    fellow: {
      type: Object,
      default: null
    },
    avatarResolver: {
      type: Function,
      default: null
    },
    avatarErrorHandler: {
      type: Function,
      default: null
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      activeTab: 'personal',
      activeFellowshipIndex: 0
    }
  },
  computed: {
    displayName() {
      if (!this.fellow) return 'Unknown Fellow'
      return this.fellow.full_name || this.fellow.name || 'Unknown Fellow'
    },
    fellowDetails() {
      return this.fellow?.fellow_details || {}
    },
    currentJobTitle() {
      return this.fellowDetails.current_job_title || 'None Provided'
    },
    countryName() {
      return this.resolveCountryName(this.fellow?.country) || 'Not provided'
    },
    resolvedAvatar() {
      if (!this.fellow) return ''
      if (this.avatarResolver) return this.avatarResolver(this.fellow)
      return this.fellow.avatar_url || this.fellow.avatar || ''
    },
    fellowshipEntries() {
      const details = this.fellow?.fellowship_details

      // Newest call year first (Fellowship 1), oldest last
      if (Array.isArray(details)) return sortFellowshipsByCallYear(details)
      if (details && typeof details === 'object') return [details]

      return []
    },
    activeFellowshipEntry() {
      return this.fellowshipEntries[this.activeFellowshipIndex] || null
    },
    hostOrganisations() {
      // The fellow's host institution(s) for this specific fellowship come
      // from the "Host Organisation" entries collected on the fellowship
      // edit form (stored as fellowship_details.collaborators), not from
      // fellowDetails.current_organisation - that field is the fellow's
      // present-day employer and can legitimately differ from where they
      // were hosted.
      let collaborators = this.activeFellowshipEntry?.collaborators
      if (!collaborators) return []

      if (typeof collaborators === 'string') {
        try {
          collaborators = JSON.parse(collaborators)
        } catch (error) {
          return []
        }
      }

      if (!Array.isArray(collaborators)) return []

      return collaborators
        .map(collaborator => {
          if (collaborator && typeof collaborator === 'object') {
            return collaborator.institution || collaborator.name || ''
          }
          return String(collaborator || '')
        })
        .map(name => name.trim())
        .filter(Boolean)
    }
  },
  watch: {
    modelValue(isOpen) {
      if (!isOpen) return
      this.activeTab = 'personal'
      this.activeFellowshipIndex = 0
    },
    fellow() {
      this.activeTab = 'personal'
      this.activeFellowshipIndex = 0
    }
  },
  methods: {
    handleAvatarError(event) {
      if (!event?.target) return
      if (this.avatarErrorHandler) {
        this.avatarErrorHandler(event, this.fellow)
      }
    },
    close() {
      this.$emit('update:modelValue', false)
    },
    resolveCountryName(country) {
      return getCountryName(country)
    },
    getFellowshipTypeLabel(callId) {
      return getFellowshipTypeDescription(callId)
    },
    getProgramLabel(programme) {
      const labels = { '1': 'EDCTP1', '2': 'EDCTP2', '3': 'Global Health EDCTP3' }
      return labels[String(programme)] || programme
    }
  }
}
</script>

<style scoped>
.fellow-preview-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.fellow-preview-dialog {
  width: min(1080px, 100%);
  max-height: calc(100vh - 48px);
  overflow: auto;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 16px 60px rgba(0, 0, 0, 0.25);
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border: none;
  background: linear-gradient(135deg, #1a8090 0%, #26d0ce 100%);
  position: relative;
}

.header-profile {
  display: flex;
  align-items: center;
  gap: 14px;
}

.avatar-wrap {
  position: relative;
}

.avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid rgba(255, 255, 255, 0.3);
}

.avatar-status {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 16px;
  height: 16px;
  background: #28a745;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
}

.title {
  margin: 0;
  font-size: var(--font-size-xl);
  color: #ffffff;
  font-weight: var(--font-weight-bold);
}

.subtitle {
  margin: 4px 0 0;
  color: rgba(255, 255, 255, 0.9);
}

.close-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  border: none;
  background: transparent;
  font-size: var(--font-size-2xl);
  line-height: var(--line-height-tight);
  color: #ffffff;
  cursor: pointer;
  opacity: 0.8;
}

.close-btn:hover {
  opacity: 1;
}

.dialog-body {
  padding: 24px;
  background: #f8f9fa;
}

.tab-nav {
  display: flex;
  gap: 8px;
  border-bottom: 2px solid #dee2e6;
  padding-bottom: 12px;
  margin-bottom: 16px;
}

.tab-btn {
  border: 1px solid transparent;
  background: transparent;
  color: #6c757d;
  padding: 10px 14px;
  border-radius: 6px 6px 0 0;
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  transition: all 0.2s ease;
}

.tab-btn.active {
  color: #1a8090;
  background: #ffffff;
  border-color: #dee2e6 #dee2e6 #ffffff;
}

.tab-btn.compact {
  padding: 6px 10px;
  font-size: var(--font-size-sm);
}

.grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 12px;
}

.grid-2col {
  grid-template-columns: repeat(2, 1fr);
}

.grid + .grid {
  margin-top: 12px;
}

.info-box {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 14px;
  background: #ffffff;
}

.label {
  margin: 0 0 6px;
  font-size: var(--font-size-xs);
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #7a8f90;
  font-weight: var(--font-weight-bold);
}

.value {
  margin: 0;
  color: #2f3f40;
  /* Free-text fields (e.g. Results & Outcomes) are plain textareas, so the
     paragraph breaks the fellow typed only survive if we honour them here. */
  white-space: pre-line;
}

.section-block {
  margin-top: 18px;
}

.section-title {
  margin: 0 0 10px;
  font-size: var(--font-size-base);
  color: #495057;
}

.fellowship-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.empty-copy {
  margin: 0;
  color: #7a8f90;
}

@media (max-width: 768px) {
  .fellow-preview-overlay {
    padding: 10px;
  }

  .dialog-header,
  .dialog-body {
    padding: 14px;
  }

  .avatar {
    width: 52px;
    height: 52px;
  }

  .title {
    font-size: var(--font-size-lg);
  }

  .grid {
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 10px;
  }

  .grid-2col {
    grid-template-columns: 1fr;
  }

  .info-box {
    padding: 12px;
  }

  .label {
    font-size: var(--font-size-xs);
    margin-bottom: 4px;
  }

  .value {
    font-size: var(--font-size-sm);
  }
}

@media (max-width: 480px) {
  .grid {
    grid-template-columns: 1fr;
    gap: 8px;
  }

  .info-box {
    padding: 10px;
  }

  .label {
    font-size: var(--font-size-xs);
    margin-bottom: 3px;
  }

  .value {
    font-size: var(--font-size-sm);
  }

  .dialog-header {
    flex-direction: column;
    text-align: center;
    gap: 12px;
  }

  .header-profile {
    flex-direction: column;
    gap: 8px;
  }

  .title {
    font-size: var(--font-size-md);
  }

  .subtitle {
    margin-top: 2px;
  }

  .close-btn {
    position: static;
  }

  .tab-nav {
    flex-direction: column;
    gap: 4px;
  }

  .tab-btn {
    padding: 8px 10px;
    font-size: var(--font-size-sm);
  }

  .fellowship-tabs {
    flex-direction: column;
    gap: 6px;
  }

  .tab-btn.compact {
    padding: 6px 8px;
    font-size: var(--font-size-sm);
  }
}
</style>
